import { useCallback, useMemo, useRef, useState } from "react";
import UserClientForm, {
  USER_CLIENT_FORM_ID,
} from "../components/UserClientForm";
import UserBusinessForm, {
  USER_BUSINESS_FORM_ID,
} from "../components/UserBusinessForm";
import UserEmployeeForm, {
  USER_ADMIN_FORM_ID,
  USER_EMPLOYEE_FORM_ID,
} from "../components/UserEmployeeForm";
import { AccountRole } from "../../../utils/types";
import "../../../modals/Modal.css";
import {
  StepNavPanel,
  type StepNavPanelHandle,
} from "../../../modals/StepNavPanel";

const ROLE_STEP_LABELS = ["Client", "Employee", "Business", "Admin"] as const;

const ROLE_FORM_IDS = [
  USER_CLIENT_FORM_ID,
  USER_EMPLOYEE_FORM_ID,
  USER_BUSINESS_FORM_ID,
  USER_ADMIN_FORM_ID,
] as const;

const ROLE_TITLE_LABELS = [
  "Create Client",
  "Create Employee",
  "Create Business User",
  "Create Admin",
] as const;

function CreateUserFormByRole({
  roleIndex,
  onSubmitChange,
}: {
  roleIndex: number;
  onSubmitChange: (isSubmitting: boolean) => void;
}) {
  switch (roleIndex) {
    case 0:
      return <UserClientForm onSubmitChange={onSubmitChange} />;
    case 1:
      return <UserEmployeeForm onSubmitChange={onSubmitChange} />;
    case 2:
      return <UserBusinessForm onSubmitChange={onSubmitChange} />;
    case 3:
      return (
        <UserEmployeeForm
          role={AccountRole.Admin}
          onSubmitChange={onSubmitChange}
        />
      );
    default:
      return <UserClientForm onSubmitChange={onSubmitChange} />;
  }
}

function RoleSelectStep() {
  return (
    <div className="create-user-role-step">
      <p className="create-user-role-hint">Choose a role to continue</p>
    </div>
  );
}

export function CreateUserModalBody() {
  const carouselRef = useRef<StepNavPanelHandle>(null);
  const [selectedRoleIndex, setSelectedRoleIndex] = useState(0);
  const [panelStep, setPanelStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const modalTitle =
    panelStep === 0 ? "Create User" : ROLE_TITLE_LABELS[selectedRoleIndex];

  const handleChooseRole = useCallback((index: number) => {
    setSelectedRoleIndex(index);
    setPanelStep(1);
    carouselRef.current?.goToStep(1);
  }, []);

  const handleBackToRoles = useCallback(() => {
    setPanelStep(0);
    carouselRef.current?.goToStep(0);
  }, []);

  const steps = useMemo(
    () => [
      {
        content: <RoleSelectStep />,
        footer: (
          <div
            className="create-user-role-buttons buttons-footer"
            role="group"
            aria-label="User role"
          >
            {ROLE_STEP_LABELS.map((label, index) => (
              <button
                key={label}
                type="button"
                className="btn create-user-role-btn"
                onClick={() => handleChooseRole(index)}
              >
                {label}
              </button>
            ))}
          </div>
        ),
      },
      {
        content: (
          <div className="create-user-form-step">
            <CreateUserFormByRole
              roleIndex={selectedRoleIndex}
              onSubmitChange={(isSubmitting: boolean) =>
                setIsSubmitting(isSubmitting)
              }
            />
          </div>
        ),
        footer: (
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={handleBackToRoles}
            >
              Cancel
            </button>
            <button
              type="submit"
              form={ROLE_FORM_IDS[selectedRoleIndex]}
              className="btn equal-btns"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create"}
            </button>
          </div>
        ),
      },
    ],
    [handleBackToRoles, handleChooseRole, isSubmitting, selectedRoleIndex],
  );

  return (
    <>
      <h3>{modalTitle}</h3>
      <StepNavPanel
        ref={carouselRef}
        steps={steps}
        className="modal-step-nav"
      />
    </>
  );
}
